"use client";
import { useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { authApi } from "@/api/auth";
import { logger } from "@/utils/logger";

interface ActivityTrackerOptions {
  // Time in milliseconds before considering user inactive (default: 5 minutes)
  inactivityThreshold?: number;
  // Time in milliseconds between activity checks (default: 1 minute)
  checkInterval?: number;
  // Time in milliseconds before token expires to refresh (default: 10 minutes)
  refreshThreshold?: number;
}

export const useActivityTracker = (options: ActivityTrackerOptions = {}) => {
  const {
    inactivityThreshold = 1 * 60 * 1000, // 5 minutes
    checkInterval = 60 * 1000, // 1 minute
    refreshThreshold = 10 * 60 * 1000, // 10 minutes
  } = options;

  const { token, user } = useAuth();
  const lastActivityRef = useRef<number>(Date.now());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  // Track user activity
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Check if user is active
  const isUserActive = useCallback(() => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;

    return timeSinceLastActivity < inactivityThreshold;
  }, [inactivityThreshold]);

  // Refresh token if needed
  const refreshTokenIfNeeded = useCallback(async () => {
    if (!token || !user || isRefreshingRef.current) return;

    try {
      // Safely parse JWT token
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) {
        logger.warn("Invalid token format");
        return;
      }

      const tokenData = JSON.parse(atob(tokenParts[1]));
      const tokenExp = tokenData.exp * 1000; // Convert to milliseconds
      const timeUntilExpiry = tokenExp - Date.now();

      // If token expires within refresh threshold and user is active, refresh it
      if (timeUntilExpiry < refreshThreshold && isUserActive()) {
        isRefreshingRef.current = true;
        try {
          // Call refresh token endpoint
          const response = await authApi.refreshToken();
          if (!response.token) {
            logger.warn("No token returned from refresh token endpoint");
            return;
          }
          // Update the token in localStorage
          localStorage.setItem("token", response.token);

          // Dispatch a custom event to notify AuthContext about token update
          window.dispatchEvent(
            new CustomEvent("auth:token-refreshed", {
              detail: { token: response.token },
            })
          );

          logger.debug("Token refreshed due to user activity");
        } catch (refreshError) {
          logger.error("Token refresh failed:", refreshError);
          // If refresh fails, the user will be logged out on next API call
        }
      }
    } catch (error) {
      logger.error("Error checking token expiration:", error);
      // If we can't parse the token, it might be invalid
      if (error instanceof SyntaxError) {
        logger.warn("Token appears to be corrupted, user will be logged out");
      }
    } finally {
      isRefreshingRef.current = false;
    }
  }, [token, user, refreshThreshold, isUserActive]);

  // Set up activity tracking
  useEffect(() => {
    if (!token || !user) return;

    // Reset activity time when user logs in
    lastActivityRef.current = Date.now();

    // Set up interval to check for token refresh
    intervalRef.current = setInterval(refreshTokenIfNeeded, checkInterval);

    // Track various user activities
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "focus",
      "blur",
    ];

    // Add event listeners for user activity
    activityEvents.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      // Clean up interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Remove event listeners
      activityEvents.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [token, user, updateActivity, refreshTokenIfNeeded, checkInterval]);

  // Reset activity when component mounts or user changes
  useEffect(() => {
    if (user) {
      lastActivityRef.current = Date.now();
    }
  }, [user]);

  return {
    isUserActive,
    lastActivity: lastActivityRef.current,
    updateActivity,
  };
};
